import {
  Avatar,
  Box,
  Checkbox,
  Container,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import GlobalButton from "../../../components/CustomButton";
import SubscriptionPurchaseDialog from "../../../components/subscriptionPaymentDialog.js";
import { companyPurchaseSubscripton } from "../../../services/company/authenticationApiFn";
import {
  createCompanyServices,
  getAllServicesWithSubServices,
  getCompanyPreSelectedServices,
} from "../../../services/company/serviceCategoryApiFn";
import { handleOpenSnackBar } from "../../../utils";
import NavigationDrawer from "../../../components/NavigationDrawer/index.js";
import AppContainer from "../../../components/AppContainer/index.js";
import AppLoader from "../../../components/AppLoader/index.js";
import { useDispatch, useSelector } from "react-redux";
import { setActiveTab } from "../../../lib/slices/activeTabSlice.js";
import { setUserData } from "../../../lib/slices/userSlice.js";
import { STRIPE_KEY } from "../../../constants/Variables.js";
import { t } from "i18next";
const stripePromise = loadStripe(STRIPE_KEY);

const CompanyAllServicesPage = () => {
  const { state } = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const openSubscriptionDialog = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [allServices, setAllServices] = useState([]);
  const activeTab = useSelector((state) => state.activeTab.tabs);
  const userData = useSelector((state) => state.userData.user);
  const { currentLanguage } = useSelector((state) => state.translationReducer);

  const mainService = state?.subscription?.features[1]?.specifics?.maxServices;
  const subService =
    state?.subscription?.features[1]?.specifics?.maxSubServices;

  useEffect(() => {
    if (!state) {
      navigate(-1);
    } else {
      fetchAllServiceWithSubServices();
    }
  }, [state]);

  const fetchAllServiceWithSubServices = async () => {
    try {
      setIsLoading(true);

      // Fetch all available services with sub-services
      const response = await getAllServicesWithSubServices();
      if (response?.data?.status === "success") {
        const allServicesData = response?.data?.data;

        // Fetch user's selected services from the CompanyService model
        const selectedResponse = await getCompanyPreSelectedServices(
          state.user
        ); // Assuming a function that fetches user selections
        const selectedServices = selectedResponse?.data?.data || [];

        // Map over the available services and mark as selected if in user’s selections
        const tempArray = allServicesData.map((service) => {
          const isMainServiceSelected = selectedServices.some(
            (selected) => selected.mainServiceId === service._id
          );

          return {
            ...service,
            isSelected: isMainServiceSelected,
            subservices: service.subservices.map((sub) => ({
              ...sub,
              isSelected: selectedServices.some(
                (selected) => selected.serviceSubCategoryId === sub._id
              ),
            })),
          };
        });

        setAllServices(tempArray);
      } else {
        handleOpenSnackBar("error", response?.data?.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error?.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectMainService = (index) => {
    const updatedServices = [...allServices];
    const mainService = updatedServices[index];
    mainService.isSelected = !mainService.isSelected;
    // If main service is deselected, deselect all subservices
    if (!mainService.isSelected) {
      mainService.subservices = mainService.subservices.map((sub) => ({
        ...sub,
        isSelected: false,
      }));
    }
    setAllServices(updatedServices);
  };

  const handleSelectSubService = (mainIndex, subIndex) => {
    const updatedServices = [...allServices];
    updatedServices[mainIndex].subservices[subIndex].isSelected =
      !updatedServices[mainIndex].subservices[subIndex].isSelected;
    setAllServices(updatedServices);
  };

  const openSubscriptionPurchaseDialog = () => {
    const selectedServices = allServices
      .filter((service) => service.isSelected)
      .map((service) => ({
        services: service._id,
        subServices: service.subservices
          .filter((sub) => sub.isSelected)
          .map((sub) => sub._id),
      }));

    // Ensure at least one main service is selected
    if (selectedServices.length === 0) {
      handleOpenSnackBar("error", t("selectAtLeastOneMainService"));
      return;
    }

    // Ensure at least one sub-service is selected for any of the selected main services
    if (selectedServices.every((service) => service.subServices.length === 0)) {
      handleOpenSnackBar("error", t("selectAtLeastOneSubService"));
      return;
    }

    // Ensure the total number of selected main services does not exceed the subscription limit
    if (selectedServices.length > mainService) {
      handleOpenSnackBar(
        "error",
        t("maxOneMainService", {
          mainServiceCount: mainService,
        })
      );
      return;
    }

    // Check that each selected main service has no more than 2 sub-services selected
    for (const service of selectedServices) {
      if (service.subServices.length > subService) {
        handleOpenSnackBar(
          "error",
          t("maxTwoSubServices", {
            subServiceCount: subService,
          })
        );
        return;
      }
    }
    openSubscriptionDialog.current.openDialog({
      subscription: state?.subscription,
      type: "upgrade",
      selectedServices,
    });
  };

  const handlePurchaseSubscription = async (params) => {
    try {
      let payload = {
        subscriptionId: state?.subscription?._id,
        companyId: state?.user,
        tokenId: params?.tokenId,
        services: params.selectedServices,
        subscriptionType: params.type,
        type: "Purchase",
      };

      setIsLoading(true);
      const response = await companyPurchaseSubscripton(payload);

      if (response.data.status === "success") {
        const servicepayload = {
          services: params.selectedServices,
          companyId: state.user,
        };

        const nestRes = await createCompanyServices(servicepayload);

        if (nestRes.data.status === "success") {
          openSubscriptionDialog.current.closeDialog();
          const updatedData = {
            ...userData,
            services: response.data.data.services,
            subscriptionId: state?.subscription?._id,
          };
          localStorage.setItem("user-data", JSON.stringify(updatedData));
          dispatch(setUserData(updatedData));
          dispatch(
            setActiveTab({ ...activeTab, compnay_subscription: "invoice" })
          );
          handleOpenSnackBar("success", response.data.message);
          navigate("/company/subscription");
        } else {
          handleOpenSnackBar("error", t("updateError"));
        }
      } else {
        handleOpenSnackBar("error", response.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <NavigationDrawer>
      <AppLoader isVisible={isLoading} />
      <AppContainer
        primaryHeading={t("updateSubscription")}
        secondaryHeading={t("upgradeServicesDescription")}
        showBackButton={true}
        handleBack={() => {
          navigate("/company/subscription");
          dispatch(
            setActiveTab({ ...activeTab, compnay_subscription: "subscription" })
          );
        }}
      >
        <Paper sx={{ p: 2 }}>
          <Stack spacing={2}>
            {allServices?.map((item, index) => (
              <Stack
                key={index}
                direction="column"
                spacing={1}
                bgcolor="#f1f1f1"
                p={1}
                border="1px solid #e9e8e8"
              >
                <Stack direction="row" spacing={1}>
                  <Checkbox
                    checked={item.isSelected}
                    onChange={() => handleSelectMainService(index)}
                  />
                  <Stack direction="row" spacing={1}>
                    <Avatar
                      src={item.image}
                      sx={{
                        height: 70,
                        width: 70,
                        borderRadius: "6px",
                      }}
                    />
                    <Box>
                      <Typography
                        variant="body1"
                        fontWeight={600}
                        textTransform="capitalize"
                      >
                        {item.name[currentLanguage]}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {item.description[currentLanguage]}
                      </Typography>
                    </Box>
                  </Stack>
                </Stack>

                {/* Subservices */}
                {item.isSelected &&
                  item.subservices.map((sub, subIndex) => (
                    <Stack
                      key={subIndex}
                      direction="row"
                      spacing={1}
                      pl={4}
                      alignItems="center"
                      bgcolor={"#ffffff"}
                      py={1}
                    >
                      <Checkbox
                        checked={sub.isSelected}
                        onChange={() => handleSelectSubService(index, subIndex)}
                      />
                      <Avatar
                        src={sub.activeIcon}
                        sx={{
                          height: 50,
                          width: 50,
                          borderRadius: "6px",
                          objectFit: "contain",
                        }}
                      />
                      <Box>
                        <Typography
                          variant="body1"
                          fontWeight={600}
                          textTransform={"capitalize"}
                        >
                          {sub.name[currentLanguage]}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {sub.description[currentLanguage]}
                        </Typography>
                      </Box>
                    </Stack>
                  ))}
              </Stack>
            ))}
          </Stack>

          <Stack my={3} alignItems={"flex-end"}>
            <GlobalButton
              btnText={"update"}
              handleClickBtn={openSubscriptionPurchaseDialog}
              styleProps={{
                width: 150,
                height: 40,
              }}
            />
          </Stack>
        </Paper>

        <Elements stripe={stripePromise}>
          <SubscriptionPurchaseDialog
            ref={openSubscriptionDialog}
            onPayment={handlePurchaseSubscription}
          />
        </Elements>
      </AppContainer>
    </NavigationDrawer>
  );
};

export default CompanyAllServicesPage;
